import { ImageName } from "@mcleod/images";
import { Clipboard, DateUtil, FileUtil, ModelRow } from "@mcleod/core";
import { AutogenLayoutEdiRawDataSlideout } from "./autogen/AutogenLayoutEdiRawDataSlideout";
import {
    ClickEvent, DataSource, HorizontalSpacer, Image, ImageType, Label, Layout, Panel, SlideoutDecorator, Snackbar, Tab, Table, TableRowMode,
    Tabset, Textbox
} from "@mcleod/components";

interface EdiRawDataSlideoutProps {
    tenderId?: string;
    shipmentStatusId?: string;
    invoiceId?: string;
    interchangeId?: string;
    replyCreated?: boolean;
    templateType?: string;
    title: string;
    rawData?: boolean;
    buttonClicked?: string;
    isTranSet?: boolean;
    indexTable?: number;
    selectAllInterchange?: boolean;
    fromViewEdiOrder?: boolean;
}

export class EdiRawDataSlideout extends AutogenLayoutEdiRawDataSlideout {
    private _inboundId: string;
    private _shipmentStatusId: string;
    private _billingId: string;
    private _interchangeId: string;
    private _replyCreated: string;
    private rawDataHasLoaded = false;
    private ediToEnglishHasLoaded = false;

    override onLoad(): void {
        this.styleAllTabs();
        this.setupTabClickListeners();
    }

    private styleAllTabs(): void {
        const tabs = [
            this.tabErrors,
            this.tabReplyErrors,
            this.tabStatusErrors,
            this.tabBillingErrors
        ];

        tabs.forEach(tab => this.styleTab(tab));
    }

    private setupTabClickListeners(): void {
        this.tabRawData.heading.addClickListener(async () => {
            if (!this.rawDataHasLoaded) {
                this.tabsetRawDataPanels.selectedIndex = -1;
                await this.searchWithActiveId(this.sourceEdiRawData);
                this.tabsetRawDataPanels.selectedIndex = this.inboundId ? 0 : 1;
            }
        });

        this.tabDatafusionToEnglish.heading.addClickListener(async () => {
            if (!this.ediToEnglishHasLoaded) {
                await this.searchWithActiveId(this.sourceEdiToEnglish);
            }
        });
    }

    private async searchWithActiveId(dataSource: DataSource<any>): Promise<void> {
        const searchParams = this.getSearchParams();
        await dataSource.search(searchParams);
    }

    private getSearchParams(): any {
        if (this.inboundId) {
            return { tender_id: this.inboundId };
        } else if (this.shipmentStatusId) {
            return { shipment_status_id: this.shipmentStatusId };
        } else if (this.interchangeId) {
            return { interchange_id: this.interchangeId };
        } else if (this.billingId) {
            return { billing_id: this.billingId };
        }
        return {};
    }

    beforeExecution() {
        this.buttonRawData.busy = true;
        this.buttonRawData.visible = true;
        this.tabsetRawDataPanels.visible = false;
    }

    afterExecution() {
        // Set up raw data tabs
        this.setRawDataTabs();

        // Process all error tabs with a more concise approach
        const errorMappings = [
            { fieldName: "tender_errors", tab: this.tabErrors, table: this.tableTenderErrors },
            { fieldName: "reply_errors", tab: this.tabReplyErrors, table: this.tableReplyErrors },
            { fieldName: "status_errors", tab: this.tabStatusErrors, table: this.tableStatusErrors },
            { fieldName: "billing_errors", tab: this.tabBillingErrors, table: this.tableBillingErrors }
        ];

        errorMappings.forEach(mapping => {
            this.setRawDataErrors(mapping.fieldName, mapping.tab, mapping.table);
        });

        // Reset UI state
        this.buttonRawData.busy = false;
        this.buttonRawData.visible = false;
        this.tabsetRawDataPanels.visible = true;
        this.rawDataHasLoaded = true;
    }

    beforeExecutionEdiToEnglish() {
        this.buttonEDIToEnglish.busy = true;
        this.buttonEDIToEnglish.visible = true;
        this.tabsetEDItoEnglish.visible = false;
    }

    async afterExecutionEdiToEnglish() {
        // Reset UI state
        this.updateUiState();

        // Select appropriate tab based on available data
        this.selectAppropriateTab();

        // Set up PDF content for all panels
        this.setupAllPdfPanels();

        this.ediToEnglishHasLoaded = true;
    }

    private updateUiState() {
        this.setTabVisibleEdiToEnglish();
        this.buttonEDIToEnglish.busy = false;
        this.buttonEDIToEnglish.visible = false;
        this.tabsetEDItoEnglish.visible = true;
        this.tabsetEDItoEnglish.selectedIndex = -1;
    }

    private selectAppropriateTab() {
        const activeRow = this.sourceEdiToEnglish?.activeRow;
        if (!activeRow) return;

        if (activeRow.get("tender_pdf")) {
            this.tabsetEDItoEnglish.selectedIndex = 0;
        } else if (activeRow.get("interchange_pdf")) {
            this.tabsetEDItoEnglish.selectedIndex = this.tabsetEDItoEnglish.indexOf(this.tabDatafusionInterchange);
        } else if (activeRow.get("billing_pdf")) {
            this.tabsetEDItoEnglish.selectedIndex = this.tabsetEDItoEnglish.indexOf(this.tabBillingEnglish);
        } else {
            this.tabsetEDItoEnglish.selectedIndex = 1;
        }
    }

    private setupAllPdfPanels() {
        const pdfMappings = [
            { field: "tender_pdf", panel: this.panelPdf },
            { field: "status_pdf", panel: this.panelStatusPdf },
            { field: "reply_pdf", panel: this.panelReplyPdf },
            { field: "func_ack_pdf", panel: this.panelFuncAckPdf },
            { field: "interchange_pdf", panel: this.panelInterchangePdf },
            { field: "billing_pdf", panel: this.panelBillingPdf }
        ];

        pdfMappings.forEach(mapping => {
            this.setPdf(mapping.field, mapping.panel);
        });
    }

    setTabVisible(rawDataValue: string, directionValue: string, datasource: DataSource<ModelRow<any>>, tab: Tab): void {
        if (!datasource?.activeRow?.get(rawDataValue, null)) {
            tab.visible = false;
        } else {
            tab.visible = true;
            if (directionValue !== null) {
                const tenderDirection = datasource?.activeRow?.get(directionValue);
                if (tenderDirection) {
                    tab.caption = tenderDirection + " " + tab.caption;
                }
            }
        }
    }

    setTabVisibleTabset(rawDataValue: string, datasource: DataSource<ModelRow<any>>, tab: Tab, tabset: Tabset, countInbound: string) {
        if (!datasource?.activeRow?.get(rawDataValue, null)) {
            tab.visible = false;
        } else {
            tab.visible = true;
            const count = datasource?.activeRow?.get(countInbound, 0);
            if (count) {
                tab.caption = tab.caption + " " + count;
            } else {
                tab.caption = tab.caption + " " + 0;
            }
            const list = datasource?.activeRow?.get(rawDataValue, null);
            tabset.removeAll();
            list.forEach((item) => {
                const panel: Panel = this.createPanel(tab, tabset);
                const tabCreated: Tab = this.createTab(item, tabset);
                const textbox: Textbox = this.createTextbox(item, tab, tabset);
                panel.components = [textbox];
                tabCreated.components = [panel]
                tabset.add(tabCreated)
            });
        }
    }

    setGeneratedLabelVisible(rawDataValue: string, directionValue: string, datasource: DataSource<ModelRow<any>>, label: Label, horizontalspacer: HorizontalSpacer, date: string, replyCreated?: string): void {
        label.visible = false;
        horizontalspacer.visible = false;
        if (datasource?.activeRow?.get(rawDataValue)) {
            const tenderDirection = datasource?.activeRow?.get(directionValue);
            if (tenderDirection !== "Inbound") {
                label.visible = true;
                horizontalspacer.visible = true;
                label.text = "Data generated at " + date;
            }
            if (replyCreated !== null && replyCreated !== undefined && tenderDirection === "Inbound" && replyCreated === "N") {
                label.visible = true;
                label.visible = true;
                horizontalspacer.visible = true;
                label.text = "Tender has not been accepted/declined. Data generated at " + date;
            }
        }
    }

    setTabVisibleEdiToEnglish(): void {
        const tabMappings = [
            { dataField: "tender_pdf", directionField: "tender_direction", tab: this.tabTenderEnglish },
            { dataField: "status_pdf", directionField: "status_direction", tab: this.tabStatusEnglish },
            { dataField: "reply_pdf", directionField: "reply_direction", tab: this.tabReplyEnglish },
            { dataField: "func_ack_pdf", directionField: "func_ack_direction", tab: this.tabFunctionalAckEnglish },
            { dataField: "interchange_pdf", directionField: null, tab: this.tabDatafusionInterchange },
            { dataField: "billing_pdf", directionField: "billing_direction", tab: this.tabBillingEnglish }
        ];

        tabMappings.forEach(mapping => {
            this.setTabVisible(mapping.dataField, mapping.directionField, this.sourceEdiToEnglish, mapping.tab);
        });
    }

    setRawDataTabs(): void {
        const date = DateUtil.formatDateTime(DateUtil.parseDateTime(new Date()), "yyyy-MM-dd HH:mm:ss");

        // Configure standard tabs
        const rawDataTabMappings = [
            { dataField: "tender_raw_data", directionField: "tender_direction", tab: this.tabTender },
            { dataField: "status_raw_data", directionField: "status_direction", tab: this.tabStatus },
            { dataField: "reply_raw_data", directionField: "reply_direction", tab: this.tabReply },
            { dataField: "func_ack_raw_data", directionField: "func_ack_direction", tab: this.tabFunctionalAck },
            { dataField: "interchange_raw_data", directionField: null, tab: this.tabInterchange },
            { dataField: "billing_raw_data", directionField: "billing_direction", tab: this.tabEdiBilling }
        ];

        rawDataTabMappings.forEach(mapping => {
            this.setTabVisible(mapping.dataField, mapping.directionField, this.sourceEdiRawData, mapping.tab);
        });

        // Configure tabsets
        const tabsetMappings = [
            { dataField: "functional_data_raw_data", tab: this.tabFunctionalData, tabset: this.tabsetFunctionalData, countField: "functional_data_count" },
            { dataField: "transaction_set_raw_data", tab: this.tabTransactionSet, tabset: this.tabsetTransactionSet, countField: "transaction_set_count" }
        ];

        tabsetMappings.forEach(mapping => {
            this.setTabVisibleTabset(mapping.dataField, this.sourceEdiRawData, mapping.tab, mapping.tabset, mapping.countField);
        });

        // Configure generated labels
        const labelMappings = [
            { dataField: "tender_raw_data", directionField: "tender_direction", label: this.labelGeneratedData, spacer: this.horizontalspacerRawTender, replyCreated: null },
            { dataField: "tender_raw_data", directionField: "tender_direction", label: this.labelGeneratedDataReply, spacer: this.horizontalspacerRawReply, replyCreated: this.replyCreated },
            { dataField: "status_raw_data", directionField: "status_direction", label: this.labelGeneratedDataStatus, spacer: this.horizontalspacerRawStatus, replyCreated: null },
            { dataField: "billing_raw_data", directionField: "billing_direction", label: this.labelGeneratedDataBilling, spacer: this.horizontalspacerRawBilling, replyCreated: null }
        ];

        labelMappings.forEach(mapping => {
            this.setGeneratedLabelVisible(mapping.dataField, mapping.directionField, this.sourceEdiRawData, mapping.label, mapping.spacer, date, mapping.replyCreated);
        });
    }

    setRawDataErrors(errorValue: string, errorTab: Tab, errorTable: Table): void {
        errorTable.clearRows();

        const errorsLength = this.sourceEdiRawData?.activeRow?.get(errorValue)?.length;
        if (errorTab != null && errorTab["countLabel"] != null) {
            errorTab["countLabel"].text = errorsLength;
        }
        if (errorsLength > 0) {
            errorTab["countLabel"].color = "error";
            const replyErrors: string[] = this.sourceEdiRawData?.activeRow?.get(errorValue);
            replyErrors.forEach(async value => {
                await errorTable._createNewRowData().then(modelRow => {
                    modelRow.setValues({ description: value });
                    modelRow._appending = false;
                    errorTable.addRow(modelRow, { mode: TableRowMode.NONE }, { display: true, addToData: true });
                });
            });
        }
    }

    styleTab(tab: Tab): void {
        if (tab.heading != null && tab.headingLabel != null) {
            tab.heading.color = "subtle.light";
            tab.headingLabel.setProps({ color: "default", fontBold: true, fillRow: false })
            if (tab["countLabel"] == null) {
                tab["countLabel"] = new Label({
                    fontBold: true, text: "0", fillRow: true,
                    color: "primary", rowBreak: false, marginLeft: 5,
                    id: "labelErrorCount"
                });
                tab.heading.insert(tab["countLabel"], 1);
            }
        }
    }

    public get inboundId(): string {
        return this._inboundId;
    }

    public set inboundId(v: string) {
        this._inboundId = v;
    }

    public get shipmentStatusId(): string {
        return this._shipmentStatusId;
    }

    public set shipmentStatusId(v: string) {
        this._shipmentStatusId = v;
    }

    public get replyCreated(): string {
        return this._replyCreated;
    }

    public set replyCreated(v: string) {
        this._replyCreated = v;
    }

    public get interchangeId(): string {
        return this._interchangeId;
    }

    public set interchangeId(v: string) {
        this._interchangeId = v;
    }

    public get billingId(): string {
        return this._billingId;
    }

    public set billingId(v: string) {
        this._billingId = v;
    }

    copyRawData() {
        const tab: string = this.tabsetRawDataPanels.getActiveTab()?.id;

        // Create a mapping of tab IDs to their text content and display names
        const tabDataMap = {
            "tabTender": { text: this.textboxTenderRawData.text, name: "Tender" },
            "tabStatus": { text: this.textboxStatusRawData.text, name: "Status" },
            "tabReply": { text: this.textboxReplyRawData.text, name: "Reply" },
            "tabInterchange": { text: this.textboxInterchangeRawData.text, name: "Interchange" },
            "tabEdiBilling": { text: this.textboxBillingRawData.text, name: "Billing" },
            "tabFunctionalAck": { text: this.textboxFuncAckRawData.text, name: "Functional Acknowledgement" }
        };

        // Special handling for nested tabsets
        if (tab === "tabFunctionalData" || tab === "tabTransactionSet") {
            const tabset = tab === "tabFunctionalData" ? this.tabsetFunctionalData : this.tabsetTransactionSet;
            const snackBarText = tab === "tabFunctionalData" ? "Functional Data" : "Transaction Set";
            const text = this.getTextFromNestedTab(tabset.getActiveTab());

            if (text) {
                this.copyToClipboard(text, snackBarText);
                return;
            }
        }

        // Handle standard tabs
        if (tabDataMap[tab]) {
            this.copyToClipboard(tabDataMap[tab].text, tabDataMap[tab].name);
        }
    }

    /**
     * Gets text content from a nested tab component
     */
    private getTextFromNestedTab(nestedTab: Tab): string | null {
        if (!nestedTab) return null;

        const panel = nestedTab.components[0] as unknown as Panel;
        if (panel) {
            const textbox = panel.components[0] as unknown as Textbox;
            if (textbox) {
                return textbox.text;
            }
        }
        return null;
    }

    /**
     * Copies text to clipboard and shows notification
     */
    private copyToClipboard(text: string, snackBarText: string): void {
        Clipboard.copyText(text);
        Snackbar.showSnackbar(`${snackBarText} Raw Data copied to clipboard`, {
            "millisUntilDismissal": 5000
        });
    }

    setPdf(data: string, panel: Panel) {
        const imgPdf = new Image({ imageType: ImageType.PDF, fillHeight: true, minWidth: 1200, id: "image_" + data });
        const imageBytes = this.sourceEdiToEnglish?.activeRow?.get(data);
        if (imageBytes) {
            imgPdf.imageBytes = imageBytes;
            panel.removeAll();
            panel.add(imgPdf);
        }
    }

    downloadPdf(event: ClickEvent) {
        const button = event.target.id;

        // Define mapping of button IDs to file configurations
        const pdfConfigs = {
            "buttonDownloadTender": {
                field: "tender_pdf",
                filename: `Tender EDI to English ${this.inboundId}.pdf`,
                id: this.inboundId
            },
            "buttonDownloadStatus": {
                field: "status_pdf",
                filename: `Shipment Status EDI to English ${this.shipmentStatusId}.pdf`,
                id: this.shipmentStatusId
            },
            "buttonDownloadReply": {
                field: "reply_pdf",
                filename: `Reply EDI to English ${this.inboundId}.pdf`,
                id: this.inboundId
            },
            "buttonDownloadInterchange": {
                field: "interchange_pdf",
                filename: `Interchange EDI to English ${this.interchangeId}.pdf`,
                id: this.interchangeId
            },
            "buttonDownloadBilling": {
                field: "billing_pdf",
                filename: `EDI Billing EDI to English ${this.billingId}.pdf`,
                id: this.billingId
            },
            "buttonDownloadFuncAck": {
                field: "func_ack_pdf",
                filename: `Functional Acknowledgement EDI to English ${this.inboundId}.pdf`,
                id: this.inboundId
            }
        };

        // Get file config or use default (func_ack_pdf)
        const config = pdfConfigs[button] || pdfConfigs["buttonDownloadFuncAck"];

        // Get file data and download
        const file = this.sourceEdiToEnglish.activeRow.get(config.field);
        FileUtil.downloadBase64AsFile(file, config.filename);
        Snackbar.showSnackbar(`Downloaded ${config.filename}`);
    }

    private createPanel(tab: Tab, tabset: Tabset): Panel {
        const panel: Panel = new Panel({ id: "panel" + tabset.id + tab.id, fillHeight: true, fillRow: true });
        return panel;
    }

    private createTab(item: any, tabset: Tabset): Tab {
        const tabCreated: Tab = new Tab({
            id: "tab" + tabset.id + item.caption,
            fillHeight: true,
            fillRow: true,
            caption: item.caption
        });
        tabCreated.createSelector();
        tabCreated.imageName = ImageName.rawData;
        return tabCreated;
    }

    private createTextbox(item: any, tab: Tab, tabset: Tabset): Textbox {
        const textbox: Textbox = new Textbox({
            fillHeight: true,
            fillRow: true,
            multiline: true,
            captionVisible: false,
            enabled: false,
            id: "textbox" + tabset.id + tab.id
        });
        textbox._interactionEnabled = true;
        textbox.field = "rawData"
        textbox.displayData(item, null, 0);
        return textbox;
    }

    public static openSlideout(props: EdiRawDataSlideoutProps) {
        const layout = Layout.getLayout("lme/datafusion/EdiRawDataSlideout", {
            width: window.innerWidth * .60,
            backgroundColor: "defaultBackground",
            fillHeight: true,
            paddingBottom: 50
        }) as EdiRawDataSlideout;
        
        new SlideoutDecorator({
            layout,
            title: props.title,
            fillVerticalSpace: true,
            doAfterSlideIn: async (decorator: SlideoutDecorator) => {
                this.initializeLayout(layout, props);
                await this.loadAppropriateData(layout, props);
            }
        });
    }
    
    private static initializeLayout(layout: EdiRawDataSlideout, props: EdiRawDataSlideoutProps): void {
        // Initialize visibility settings
        layout.tabsetRawDataPanels.visible = false;
        layout.tabsetEDItoEnglish.visible = false;
        layout.tabDatafusionToEnglish.visible = props?.templateType !== "X" && props?.templateType !== "J";
        
        // Set IDs
        layout.inboundId = props?.tenderId;
        layout.shipmentStatusId = props?.shipmentStatusId;
        layout.billingId = props?.invoiceId;
        layout.interchangeId = props?.interchangeId;
        layout.replyCreated = props?.tenderId && props?.replyCreated ? "Y" : "N";
    }
    
    private static async loadAppropriateData(layout: EdiRawDataSlideout, props: EdiRawDataSlideoutProps): Promise<void> {
        // Define handler configurations for different ID types
        const dataHandlers = {
            tender: async () => {
                if (props?.fromViewEdiOrder) {
                    if (props?.rawData) {
                        layout.tabsetRawData.selectedIndex = layout.tabsetRawData.indexOf(layout.tabRawData);
                        let index;
                        if (props?.buttonClicked === "buttonReplyRawData") {
                            index = layout.tabsetRawDataPanels.indexOf(layout.tabReply);
                        } else if (props?.buttonClicked === "buttonFuncAckRawData") {
                            index = layout.tabsetRawDataPanels.indexOf(layout.tabFunctionalAck);
                        } else {
                            index = layout.tabsetRawDataPanels.indexOf(layout.tabTender);
                        }
                        layout.tabsetRawDataPanels.selectedIndex = -1;
                        await layout.sourceEdiRawData.search({tender_id: props?.tenderId});
                        layout.tabsetRawDataPanels.selectedIndex = index;
                    } else {
                        layout.tabsetRawData.selectedIndex = layout.tabsetRawData.indexOf(layout.tabDatafusionToEnglish);
                        await layout.sourceEdiToEnglish.search({tender_id: props?.tenderId});
                    }
                } else {
                    layout.tabsetRawData.selectedIndex = layout.tabsetRawData.indexOf(layout.tabRawData);
                    layout.tabsetRawDataPanels.selectedIndex = -1;
                    await layout.sourceEdiRawData.search({ tender_id: props?.tenderId });
                    layout.tabsetRawDataPanels.selectedIndex = layout.tabsetRawDataPanels.indexOf(layout.tabTender);
                }
            },
            
            shipmentStatus: async () => {
                if (props?.rawData) {
                    layout.tabsetRawData.selectedIndex = layout.tabsetRawData.indexOf(layout.tabRawData);
                    const index = props?.buttonClicked === "buttonFuncAckRawData" 
                        ? layout.tabsetRawDataPanels.indexOf(layout.tabFunctionalAck)
                        : layout.tabsetRawDataPanels.indexOf(layout.tabStatus);
                    
                    layout.tabsetRawDataPanels.selectedIndex = -1;
                    await layout.sourceEdiRawData.search({ shipment_status_id: props?.shipmentStatusId });
                    layout.tabsetRawDataPanels.selectedIndex = index;
                } else {
                    layout.tabsetRawData.selectedIndex = layout.tabsetRawData.indexOf(layout.tabDatafusionToEnglish);
                    await layout.sourceEdiToEnglish.search({ shipment_status_id: props?.shipmentStatusId });
                }
            },
            
            interchange: async () => {
                layout.tabsetRawData.selectedIndex = layout.tabsetRawData.indexOf(layout.tabRawData);
                if (props?.selectAllInterchange) {
                    const index = layout.tabsetRawDataPanels.indexOf(layout.tabInterchange);
                    layout.tabsetRawDataPanels.selectedIndex = -1;
                    await layout.sourceEdiRawData.search({ interchange_id: props?.interchangeId });
                    layout.tabsetRawDataPanels.selectedIndex = index;
                } else {
                    const index = layout.tabsetRawDataPanels.indexOf(
                        props?.isTranSet ? layout.tabTransactionSet : layout.tabFunctionalData
                    );
                    
                    layout.tabsetRawDataPanels.selectedIndex = -1;
                    
                    // Initialize appropriate tabset
                    if (props?.isTranSet) {
                        layout.tabsetTransactionSet.selectedIndex = -1;
                    } else {
                        layout.tabsetFunctionalData.selectedIndex = -1;
                    }
                    
                    await layout.sourceEdiRawData.search({ interchange_id: props?.interchangeId });
                    layout.tabsetRawDataPanels.selectedIndex = index;
                    
                    // Set specific tab in the nested tabset
                    if (props?.isTranSet) {
                        layout.tabsetTransactionSet.selectedIndex = props?.indexTable;
                    } else {
                        layout.tabsetFunctionalData.selectedIndex = props?.indexTable;
                    }
                }
                
            },
            
            billing: async () => {
                layout.tabsetRawData.selectedIndex = layout.tabsetRawData.indexOf(layout.tabRawData);
                const index = layout.tabsetRawDataPanels.indexOf(layout.tabEdiBilling);
                layout.tabsetRawDataPanels.selectedIndex = -1;
                await layout.sourceEdiRawData.search({ billing_id: props?.invoiceId });
                layout.tabsetRawDataPanels.selectedIndex = index;
            }
        };
        
        // Choose appropriate data loading method based on provided ID
        if (props?.tenderId) {
            await dataHandlers.tender();
        } else if (props?.shipmentStatusId) {
            await dataHandlers.shipmentStatus();
        } else if (props?.interchangeId) {
            await dataHandlers.interchange();
        } else if (props?.invoiceId) {
            await dataHandlers.billing();
        }
    }
}
